import _ from "lodash";
import React from "react";
import {
  colors,
  NavConnectIcon,
  NavFinanceIcon,
  NavManagementIcon,
  NavProdIcon,
  NavSalesIcon,
  NavResourcesIcon,
  NavReportsIcon,
} from "@commonsku/styles";
import { getNavItems } from "./csku_menu_utils";
import BaseCskuMenu from "./BaseCskuMenu";
import { getIdentityUtils } from "../utils";
import { Icon } from "@commonsku/design-system";
import { MdMotionPhotosAuto, MdLightbulbOutline, MdLightbulb } from "react-icons/md";

const iconsProps = {
  style: {
    verticalAlign: "middle",
    marginRight: 5,
  },
};

const IconLightbulb = (p) => {
  return (
    <Icon ariaLabel="Lightbulb" ariaHidden="true" {...p} size="lg">
      {p.filled ? <MdLightbulb /> : <MdLightbulbOutline />}
    </Icon>
  );
};

const filledInIcon = {
  connect: <NavConnectIcon filled {...iconsProps} />,
  sales: <NavSalesIcon filled {...iconsProps} />,
  production: <NavProdIcon filled {...iconsProps} />,
  finance: <NavFinanceIcon filled {...iconsProps} />,
  automations: (<Icon ariaHidden="true" size="lg" {...iconsProps}><MdMotionPhotosAuto /></Icon>),
  management: <NavManagementIcon filled {...iconsProps} />,
  insights: <IconLightbulb filled {...iconsProps} />,
  reports: <NavReportsIcon filled {...iconsProps} />,
  learn: <NavResourcesIcon filled {...iconsProps} />,
};

class CskuMenuV2 extends BaseCskuMenu {
  constructor(props) {
    super(props);

    this.state = {
      category: "connect",
      menuClicked: false,
    };

    this.handleClickSubMenu = this.handleClickSubMenu.bind(this);
  }

  routeMap = {
    distributor: "distributors",
    product: "products",
    client: "clients",
  };

  routes = [
    "home",
    "dashboard",
    "discover",
    "tool",
    "promostandards",
    "admin",
    "suppliers_production",
    "production_dashboard",
    "production_report",
    "production_on_demand",
    "distributor",
    "clients",
    "client",
    "suppliers",
    "products",
    "product",
    "suspended",
    "set_up",
    "collections",
    "shops",
    'shop_templates',
    "whatsnew",
    "projects",
    "promostandards_supplier",
    "marketing",
    "blog",
    "operation_billing",
    "operation_invoicing",
    "finance_billing",
    "finance_invoicing",
    "finance_payments",
    "operation_wip",
    "sales_analytics",
    "finance_analytics",
    "management_analytics",
    "sales_dashboard",
    "finance_dashboard",
    "management_dashboard",
    "support",
    "report",
    "automations",
  ];

  routesCategories = [
    {
      name: "connect",
      title: "Connect",
      icon: <NavConnectIcon {...iconsProps} />,
      type: "sub-menu",
      routes: ["home", "suppliers", "collections", "products", "discover"],
      disabled: this.props.lockedOut,
      show: true,
    },
    {
      name: "sales",
      title: "Sales",
      icon: <NavSalesIcon {...iconsProps} />,
      type: "sub-menu",
      routes: [
        "sales_dashboard",
        "projects",
        "clients",
        "commissions",
        "shops",
        "shop_templates",
        "sales_analytics",
      ],
      disabled: this.props.lockedOut || !this.userCanViewMenu("SALES"),
      show: true,
    },
    {
      name: "production",
      title: "Production",
      icon: <NavProdIcon {...iconsProps} />,
      type: "sub-menu",
      routes: [
        "production_dashboard",
        "production_report",
        ...(this.hasUserFlags("POD_ENABLED") ? ["production_on_demand"] : []),
      ],
      disabled: this.props.lockedOut || !this.userCanViewMenu("PRODUCTION"),
      show: true,
    },
    {
      name: "finance",
      title: "Finance",
      icon: <NavFinanceIcon {...iconsProps} />,
      type: "sub-menu",
      routes: [
        "finance_dashboard",
        "operation_billing",
        "operation_invoicing",
        "finance_billing",
        "finance_invoicing",
        "finance_payments",
        "operation_wip",
        "finance_analytics",
      ],
      disabled: this.props.lockedOut || !this.userCanViewMenu("FINANCE"),
      show: true,
    },
    {
      name: "automations",
      title: "Automations",
      icon: (<Icon ariaHidden="true" size="lg" {...iconsProps}><MdMotionPhotosAuto /></Icon>),
      type: "link",
      to: '/automations',
      route: "automations",
      show: this.hasCapabilities('HAS-WORKFLOW-AUTOMATION') && this.isTenant(),
      disabled: this.props.lockedOut,
    },
    {
      name: "management",
      title: "Management",
      icon: <NavManagementIcon {...iconsProps} />,
      type: "sub-menu",
      routes: ["management_dashboard", "admin", "management_analytics"],
      disabled: this.props.lockedOut || !this.userCanViewMenu("MANAGEMENT"),
      show: this.hasCapabilities(['HAS-MANAGEMENT-SECTION']),
    },
    {
      name: "insights",
      title: "Insights",
      icon: <IconLightbulb {...iconsProps} />,
      type: "sub-menu",
      routes: ["reports", "skubot_chats"],
      disabled: this.props.lockedOut || !this.hasCapabilities(['FEATURE-FULL', 'FEATURE-ESSENTIAL', 'FEATURE-ADVANCED', 'FEATURE-ENTERPRISE'], true),
      show: this.hasUserFlags(['INSIGHTS-ENABLED'], true),
    },
    {
      name: "reports",
      title: "Reports",
      icon: <NavReportsIcon {...iconsProps} />,
      type: "link",
      to: '/report',
      route: "report",
      disabled: this.props.lockedOut || !this.hasCapabilities(['FEATURE-FULL', 'FEATURE-ESSENTIAL', 'FEATURE-ADVANCED', 'FEATURE-ENTERPRISE'], true),
      show: !this.hasUserFlags(['INSIGHTS-ENABLED'], true),
    },
    {
      name: "learn",
      title: "Resources",
      icon: <NavResourcesIcon {...iconsProps} />,
      type: "sub-menu",
      routes: ["skummunity", "support", "help_articles", "university", "blog"],
      disabled: this.props.lockedOut,
      show: true,
    },
  ];

  hasUserFlags(flags, any = false) {
    const { hasUserFlags } = getIdentityUtils(this.props.identity);
    return hasUserFlags(flags, any);
  }

  hasCapabilities(capabilities, any = false) {
    const { hasCapabilities } = getIdentityUtils(this.props.identity);
    return hasCapabilities(capabilities, any);
  }

  userCanViewMenu(menu) {
    return this.hasCapabilities("VIEW-" + menu + "-MENU");
  }

  menuDisplayControlByUserFlag(flag) {
    const { hasUserFlags } = getIdentityUtils(this.props.identity);
    return hasUserFlags(flag);
  }

  isTenant() {
    const { isTenant } = getIdentityUtils(this.props.identity);
    return isTenant();
  }

  componentDidMount() {
    const curRoute = this.findRoute(this.props.scriptName);

    if (!curRoute) {
      return;
    }

    const found = this.routesCategories.filter((v) => {
      if (v.disabled) {
        return false;
      }
      if (v.type === "link") {
        return v.route === curRoute;
      }
      return (
        v.routes.indexOf(curRoute) !== -1 ||
        v.routes.indexOf(this.routeMap[curRoute]) !== -1
      );
    });

    if (found.length) {
      this.setState({ category: found[0].name });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.toggleMainMenu === false &&
      this.state.menuClicked === true
    ) {
      this.setState({ category: "", menuClicked: false });
    }
  }

  handleClickSubMenu(name, items) {
    if (this.state.category === name) {
      this.setState({ category: "", menuClicked: false });
    } else {
      this.setState({ category: name, menuClicked: true });
    }
  }

  renderCollapsed({ curRoute, isMobile, items, ulProps, toggleMainMenu }) {
    const { menuClicked, category } = this.state;

    return (
      <ul className="csku-menu-v2 vertical menu main-menu-v2-collapsed">
        {this.routesCategories
          .filter((rc) => rc.show)
          .map((c, j) => {
            const show = category === c.name && toggleMainMenu && menuClicked;
            const icon = category === c.name ? filledInIcon[c.name] : c.icon;
            const params = {
              category: c,
              className: "",
              curRoute,
              isMobile,
              items,
              ulProps: ulProps,
              linkClassName: "menu-link-v2-collapsed",
              show,
            };
            return (
              <li
                key={"menu-items-title-" + j}
                className={`menu-link-wrapper-v2-collapsed ${
                  c.disabled ? " disabled " : ""
                }`}
                onClick={(e) => {
                  if (c.type === "link") {
                    window.location.href = c.to;
                  } else {
                    this.handleClickSubMenu(c.name, items);
                  }
                }}
              >
                <a
                  href="#"
                  className="menu-link-v2-collapsed"
                  style={{
                    ...(show ? { background: colors.primary20 } : {}),
                    userSelect: "none",
                  }}
                >
                  <span className="menu-link-v2-collapsed-icon">{icon}</span>
                  <span className="menu-link-v2-collapsed-text">{c.title}</span>
                </a>
                {c.type !== "link" && this.renderCategoryMenuItems(params)}
              </li>
            );
          })}
      </ul>
    );
  }

  renderCategoryMenuItems({
    ulProps,
    isMobile,
    className,
    items,
    curRoute,
    category,
    show = true,
    linkClassName = "",
    hideLinkText = false,
  }) {
    return (
      <ul
        className={
          "vertical menu sub-menu " +
          className +
          (this.state.category === category.name && show
            ? " is-open "
            : " is-closed ") +
          (isMobile ? " mobile-menu " : "")
        }
        {...ulProps}
        style={{ ...(ulProps.style || {}) }}
        key="menu-v2-CategoryMenuItems"
      >
        {items
          .filter((v) => v.show && category.routes?.indexOf(v.route) !== -1)
          .sortBy([(v) => category.routes?.indexOf(v.route)])
          .map(({ liProps, to, title, route, children }, i) =>
            this.renderRoute({
              index: i,
              curRoute,
              liProps,
              to,
              title,
              route,
              linkClassName,
              children: !hideLinkText ? children : null,
            })
          )
          .value()}
      </ul>
    );
  }

  render() {
    const {
      identity,
      scriptName,
      className = "",
      isMobile = false,
      collapsed = false,
      toggleMainMenu = false,
      ...props
    } = this.props;
    const curRoute = this.findRoute(scriptName);
    const items = getNavItems({ identity, v2: true });
    const ulProps = _.omit(props, ["dispatch", "lockedOut"]);

    return collapsed ? (
      this.renderCollapsed({
        className,
        curRoute,
        isMobile,
        items,
        ulProps,
        toggleMainMenu,
      })
    ) : (
      <div data-test-name="nav-menu" className="csku-menu-v2">
        {this.routesCategories
          .filter((rc) => rc.show)
          .map((c, j) => {
            const icon =
              this.state.category === c.name ? filledInIcon[c.name] : c.icon;
            return (
              <ul
                key={"menu-item-parent-" + j}
                className={
                  "vertical menu parent-menu " +
                  className +
                  (this.state.category === c.name ? " is-open" : "") +
                  (c.disabled ? " disabled " : "")
                }
                {...ulProps}
              >
                <li
                  key={"menu-items-title-" + j}
                  data-test-name={`nav-menu-category-${c.name}`}
                  onClick={(e) => {
                    if (c.type === "link") {
                      window.location.href = c.to;
                    } else {
                      this.handleClickSubMenu(c.name, items);
                    }
                  }}
                >
                  <a style={{ userSelect: "none" }}>
                    {icon} {c.title}
                  </a>
                </li>
                {c.type !== "link" && (
                  <li key={"menu-items-" + j}>
                    {this.renderCategoryMenuItems({
                      category: c,
                      className,
                      curRoute,
                      isMobile,
                      items,
                      ulProps,
                    })}
                  </li>
                )}
              </ul>
            );
          })}
      </div>
    );
  }
}

export default CskuMenuV2;
